import request from '@/utils/request';
import type {
  AddTrackingNumberCommand,
  CancelOrderCommand,
  ConfirmFinalQuoteCommand,
  CreateRecycleCategoryCommand,
  CreateRecycleValuationConfigurationCommand,
  CreateReturnLogisticCommand,
  CreateSettlementRecordCommand,
  LogisticTrackingDTO,
  RecycleCategory,
  RecycleCategoryQuery,
  RecycleLogistic,
  RecycleLogisticQuery,
  RecycleOrder,
  RecycleOrderQuery,
  RecycleSettlementRecord,
  RecycleValuationConfiguration,
  RecycleValuationQuery,
  SettleOrderCommand,
  SubmitFinalQuoteCommand,
  UpdateRecycleCategoryCommand,
  UpdateRecycleValuationConfigurationCommand
} from '@/types/recycle';
import type {PaginatedResponse} from '@/types/common';

export const recycleService = {
  // ==================== 品类管理 ====================

  /**
   * 获取品类列表
   */
  getCategories: async (query: RecycleCategoryQuery): Promise<PaginatedResponse<RecycleCategory>> => {
    const response = await request.get('/api/admin/recycle-categories/list', { params: query });
    return response;
  },

  /**
   * 获取品类树形结构
   */
  getCategoryTree: async (): Promise<RecycleCategory[]> => {
    const response = await request.get('/api/admin/recycle-categories/tree');
    return response;
  },

  /**
   * 获取品类详情
   */
  getCategoryById: async (id: number): Promise<RecycleCategory> => {
    const response = await request.get(`/api/admin/recycle-categories/${id}`);
    return response;
  },

  /**
   * 创建品类
   */
  createCategory: async (data: CreateRecycleCategoryCommand): Promise<RecycleCategory> => {
    const response = await request.post('/api/admin/recycle-categories', data);
    return response;
  },

  /**
   * 更新品类
   */
  updateCategory: async (id: number, data: UpdateRecycleCategoryCommand): Promise<RecycleCategory> => {
    const response = await request.put(`/api/admin/recycle-categories/${id}`, data);
    return response;
  },

  /**
   * 删除品类
   */
  deleteCategory: async (id: number): Promise<void> => {
    await request.delete(`/api/admin/recycle-categories/${id}`);
  },

  /**
   * 更新品类状态
   */
  updateCategoryStatus: async (id: number, enabled: boolean): Promise<RecycleCategory> => {
    const response = await request.put(`/api/admin/recycle-categories/${id}/status`, null, {
      params: { enabled }
    });
    return response;
  },

  /**
   * 批量导入品类
   */
  batchImportCategories: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await request.post('/api/admin/recycle-categories/batch-import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  /**
   * 下载批量导入模板
   */
  downloadImportTemplate: async (): Promise<void> => {
    const response = await request.get('/api/admin/recycle-categories/download-template', {
      responseType: 'blob'
    });
    
    // 创建下载链接
    const url = window.URL.createObjectURL(new Blob([response], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', '回收品类批量导入模板.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * 获取子品类
   */
  getCategoryChildren: async (parentId: number): Promise<RecycleCategory[]> => {
    const response = await request.get(`/api/admin/recycle-categories/${parentId}/children`);
    return response;
  },

  // ==================== 订单管理 ====================

  /**
   * 获取订单列表
   */
  getOrders: async (query: RecycleOrderQuery): Promise<PaginatedResponse<RecycleOrder>> => {
    const response = await request.get('/api/admin/recycle-orders/search', { params: query });
    return response;
  },

  /**
   * 获取订单详情
   */
  getOrderByNo: async (orderNo: string): Promise<RecycleOrder> => {
    const response = await request.get(`/api/admin/recycle-orders/${orderNo}`);
    return response;
  },

  /**
   * 根据订单ID获取订单详情
   */
  getOrderById: async (orderId: number): Promise<RecycleOrder> => {
    const response = await request.get(`/api/admin/recycle-orders/id/${orderId}`);
    return response;
  },

  /**
   * 审核订单
   */
  reviewOrder: async (data: { orderNo: string }): Promise<boolean> => {
    const response = await request.post('/api/admin/recycle-orders/review', data);
    return response;
  },

  /**
   * 更新订单状态
   */
  updateOrderStatus: async (orderNo: string, status: string): Promise<RecycleOrder> => {
    const response = await request.put(`/api/admin/recycle-orders/${orderNo}/status`, null, {
      params: { status }
    });
    return response;
  },

  /**
   * 订单发货
   */
  orderSendOut: async (orderNo: string, data: {
    sendOutMethod: string;
    visitAddress?: string;
    visitProvince?: string;
    visitCity?: string;
    visitCounty?: string;
    visitContactName?: string;
    visitContactPhone?: string;
    visitTime?: string;
    postageSettlementMethod?: string;
    remark?: string
  }): Promise<RecycleOrder> => {
    const response = await request.post(`/api/admin/recycle-orders/${orderNo}/send-out`, data);
    return response;
  },

  /**
   * 提交最终报价
   */
  submitFinalQuote: async (orderNo: string, data: SubmitFinalQuoteCommand): Promise<RecycleOrder> => {
    const response = await request.post(`/api/admin/recycle-orders/${orderNo}/submit-final-quote`, data);
    return response;
  },

  /**
   * 用户确认或拒绝最终报价
   */
  confirmFinalQuote: async (orderNo: string, data: ConfirmFinalQuoteCommand): Promise<RecycleOrder> => {
    const response = await request.post(`/api/mini/recycle-orders/${orderNo}/confirm-final-quote`, data);
    return response;
  },

  /**
   * 结算订单
   */
  settleOrder: async (orderNo: string, data: SettleOrderCommand): Promise<RecycleOrder> => {
    const response = await request.post(`/api/admin/recycle-orders/${orderNo}/settle`, data);
    return response;
  },

  // ==================== 结算记录管理 ====================

  /**
   * 创建结算记录
   */
  createSettlementRecord: async (data: CreateSettlementRecordCommand): Promise<RecycleSettlementRecord> => {
    const response = await request.post('/api/admin/recycle-settlement-records', data);
    return response;
  },

  /**
   * 根据订单ID查询结算记录
   */
  getSettlementRecordsByOrderId: async (orderId: number): Promise<RecycleSettlementRecord[]> => {
    const response = await request.get(`/api/admin/recycle-settlement-records/order/${orderId}`);
    return response;
  },

  /**
   * 取消订单
   */
  cancelOrder: async (orderNo: string, data: CancelOrderCommand): Promise<RecycleOrder> => {
    const response = await request.post(`/api/admin/recycle-orders/${orderNo}/cancel`, data);
    return response;
  },

  /**
   * 导出订单
   */
  exportOrders: async (query: RecycleOrderQuery): Promise<Blob> => {
    const response = await request.get('/api/admin/recycle-orders/export', {
      params: query,
      responseType: 'blob'
    });
    return response;
  },

  // ==================== 物流管理 ====================

  /**
   * 获取物流列表
   */
  getLogistics: async (query: RecycleLogisticQuery): Promise<PaginatedResponse<RecycleLogistic>> => {
    const response = await request.get('/api/admin/recycle-logistics/search', { params: query });
    return response;
  },

  /**
   * 获取订单物流信息
   */
  getOrderLogistics: async (orderId: number): Promise<RecycleLogistic[]> => {
    const response = await request.get(`/api/admin/recycle-logistics/order/${orderId}`);
    return response;
  },

  /**
   * 更新物流状态
   */
  updateLogisticStatus: async (id: number, status: string): Promise<RecycleLogistic> => {
    const response = await request.put(`/api/admin/recycle-logistics/${id}/status`, null, {
      params: { status }
    });
    return response;
  },

  /**
   * 添加物流单号
   */
  addTrackingNumber: async (data: AddTrackingNumberCommand): Promise<RecycleLogistic> => {
    const response = await request.post('/api/admin/recycle-logistics/tracking', data);
    return response;
  },

  /**
   * 创建寄回物流记录
   */
  createReturnLogistic: async (data: CreateReturnLogisticCommand): Promise<RecycleLogistic> => {
    const response = await request.post('/api/admin/recycle-logistics/return', data);
    return response;
  },

  /**
   * 获取物流跟踪信息
   */
  getLogisticTracking: async (logisticId: number): Promise<LogisticTrackingDTO> => {
    const response = await request.get(`/api/admin/recycle-logistics/${logisticId}/tracking`);
    return response;
  },

  // ==================== 估价管理 ====================

  /**
   * 获取品类估价配置列表
   */
  getCategoryValuations: async (): Promise<RecycleCategory[]> => {
    const response = await request.get('/api/admin/recycle-valuation/category/list');
    return response;
  },

  /**
   * 更新品类估价配置
   */
  updateCategoryValuation: async (data: any): Promise<any> => {
    const response = await request.put('/api/admin/recycle-valuation/category/update', data);
    return response;
  },

  /**
   * 获取估价配置列表
   */
  getValuationConfigurations: async (query: RecycleValuationQuery): Promise<PaginatedResponse<RecycleValuationConfiguration>> => {
    const response = await request.get('/api/admin/recycle-valuation/configuration/list', { params: query });
    return response;
  },

  /**
   * 获取估价配置详情
   */
  getValuationConfigurationById: async (id: number): Promise<RecycleValuationConfiguration> => {
    const response = await request.get(`/api/admin/recycle-valuation/configuration/${id}`);
    return response;
  },

  /**
   * 创建估价配置
   */
  createValuationConfiguration: async (data: CreateRecycleValuationConfigurationCommand): Promise<RecycleValuationConfiguration> => {
    const response = await request.post('/api/admin/recycle-valuation/configuration', data);
    return response;
  },

  /**
   * 更新估价配置
   */
  updateValuationConfiguration: async (data: UpdateRecycleValuationConfigurationCommand): Promise<RecycleValuationConfiguration> => {
    const response = await request.put(`/api/admin/recycle-valuation/configuration/${data.id}`, data);
    return response;
  },

  /**
   * 删除估价配置
   */
  deleteValuationConfiguration: async (id: number): Promise<void> => {
    await request.delete(`/api/admin/recycle-valuation/configuration/${id}`);
  },

  /**
   * 生成估价价格
   */
  generateValuationPrice: async (data: { categoryId: number; itemValues: Record<string, string> }): Promise<number> => {
    const response = await request.post('/api/admin/recycle-valuation/generate-price', data);
    return response;
  },

  // ==================== 小程序端接口 ====================

  /**
   * 小程序端获取品类列表
   */
  getMiniCategories: async (): Promise<RecycleCategory[]> => {
    const response = await request.get('/api/mini/recycle-valuation/category/list');
    return response;
  },

  /**
   * 小程序端生成估价价格
   */
  generateMiniValuationPrice: async (data: { categoryId: number; itemValues: Record<string, string> }): Promise<number> => {
    const response = await request.post('/api/mini/recycle-valuation/generate-price', data);
    return response;
  },

  /**
   * 小程序端创建回收订单
   */
  createMiniOrder: async (data: any): Promise<RecycleOrder> => {
    const response = await request.post('/api/mini/recycle-orders/create', data);
    return response;
  },

  /**
   * 小程序端获取用户订单列表
   */
  getMiniUserOrders: async (query: any): Promise<PaginatedResponse<RecycleOrder>> => {
    const response = await request.get('/api/mini/recycle-orders/user/list', { params: query });
    return response;
  },

  /**
   * 小程序端获取订单详情
   */
  getMiniOrderDetail: async (orderNo: string): Promise<RecycleOrder> => {
    const response = await request.get(`/api/mini/recycle-orders/${orderNo}`);
    return response;
  }
};
